const React = require('react');
const { createElement: h, useState, useEffect } = React;
require('./ConfirmDialog.css');

// Minimal client-side semver helpers (mirror api/src/utils/semver.js) so the
// dialog can pre-fill the next patch and validate before calling the server.
var SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;
function parseSemver(value) {
  var m = SEMVER_RE.exec(String(value || '').trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}
function isValidSemver(value) { return parseSemver(value) !== null; }
function compareSemver(a, b) {
  var pa = parseSemver(a), pb = parseSemver(b);
  if (!pa || !pb) return null;
  for (var i = 0; i < 3; i += 1) { if (pa[i] !== pb[i]) return pa[i] > pb[i] ? 1 : -1; }
  return 0;
}
function bumpPatch(value) {
  var p = parseSemver(value);
  if (!p) return '1.0.0';
  return p[0] + '.' + p[1] + '.' + (p[2] + 1);
}

// Centered styled popup (NOT a native popup) for publishing the staging
// workflow to its LIVE (production) version. Reuses the shared .cdialog-* shell.
// Shows the current live version and requires the user to bump it before
// confirming. The parent (App) owns the data: it passes the current live
// version in and handles the publish call via onPublish(version).
//
//   currentVersion: number | null   (null = nothing published yet)
//   publishedAt:    ISO string | null (when the current live was published)
//   isPublishing:   boolean          (disables actions + shows progress)
//   error:          string           (server validation / failure message)
//   onPublish(version): publish staging → live with this version number
//   onCancel():     dismiss

function formatWhen(value) {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return '';
  }
}

function PublishLiveDialog(props) {
  var hasLive = !!(props.currentVersion && isValidSemver(props.currentVersion));
  var currentVersion = hasLive ? String(props.currentVersion) : '';
  var publishedAt = props.publishedAt || null;
  var isPublishing = !!props.isPublishing;
  var serverError = props.error || '';
  var onPublish = props.onPublish || function() {};
  var onCancel = props.onCancel || function() {};

  // Pre-fill the next PATCH version (e.g. 1.0.0 → 1.0.1), or 1.0.0 on a first
  // publish. Fully editable so the user can type a minor/major bump instead.
  var [version, setVersion] = useState(hasLive ? bumpPatch(currentVersion) : '1.0.0');
  var [localError, setLocalError] = useState('');

  useEffect(function() {
    function onKeyDown(e) {
      if (e.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return function() { document.removeEventListener('keydown', onKeyDown); };
  }, []);

  function submit() {
    var v = String(version || '').trim();
    if (!isValidSemver(v)) {
      setLocalError('Use the form MAJOR.MINOR.PATCH, e.g. 1.0.1.');
      return;
    }
    if (hasLive && compareSemver(v, currentVersion) <= 0) {
      setLocalError('Version must be greater than the current live version (' + currentVersion + ').');
      return;
    }
    setLocalError('');
    onPublish(v);
  }

  var shownError = localError || serverError;

  return h('div', { className: 'cdialog-overlay', onMouseDown: onCancel },
    h('div', {
      className: 'cdialog-box',
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      h('div', { className: 'cdialog-icon' },
        h('svg', { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
          h('path', { d: 'M12 19V6' }),
          h('path', { d: 'M5 12l7-7 7 7' })
        )
      ),
      h('div', { className: 'cdialog-title' }, 'Publish to Live'),
      h('div', { className: 'cdialog-message' },
        hasLive
          ? ('This updates the live (production) version that public runs execute. '
              + 'Variable values are not copied to the live version.')
          : ('This creates the live (production) version that public runs will execute. '
              + 'Variable values are not copied to the live version.')
      ),
      h('div', { className: 'cdialog-fields' },
        h('div', { className: 'cdialog-field' },
          h('span', { className: 'cdialog-field-label' }, 'Current live version'),
          h('div', {
            style: {
              fontSize: '13px',
              color: hasLive ? 'var(--text-primary, #e6e9ef)' : 'var(--text-secondary, #8b93a7)',
              padding: '2px 0'
            }
          },
            hasLive
              ? ('v' + currentVersion + (publishedAt ? ('  ·  ' + formatWhen(publishedAt)) : ''))
              : 'Not published yet'
          )
        ),
        h('label', { className: 'cdialog-field' },
          h('span', { className: 'cdialog-field-label' }, 'New live version'),
          h('input', {
            className: 'cdialog-field-input',
            type: 'text',
            inputMode: 'decimal',
            placeholder: '1.0.0',
            value: version,
            autoFocus: true,
            disabled: isPublishing,
            onChange: function(e) { setVersion(e.target.value); setLocalError(''); },
            onKeyDown: function(e) { if (e.key === 'Enter') submit(); }
          })
        )
      ),
      shownError
        ? h('div', {
            style: { color: 'var(--danger, #ef4444)', fontSize: '12px', marginTop: '6px', textAlign: 'center' }
          }, shownError)
        : null,
      h('div', { className: 'cdialog-actions' },
        h('button', {
          className: 'cdialog-btn cdialog-btn-cancel',
          onClick: onCancel,
          disabled: isPublishing
        }, 'Cancel'),
        h('button', {
          className: 'cdialog-btn cdialog-btn-confirm',
          onClick: submit,
          disabled: isPublishing
        }, isPublishing ? 'Publishing…' : 'Publish to Live')
      )
    )
  );
}

module.exports = PublishLiveDialog;
